import type { SignInSecondFactor, SignInStatus } from "@clerk/shared/types";
import { strings } from "@remi/localization";
import { remiLogger } from "@remi/logging";

export type SelectedSecondFactor =
  | { strategy: "totp" }
  | { strategy: "phone_code"; phoneNumberId: string; safeIdentifier: string }
  | { strategy: "backup_code" };

const toSelected = (factor: SignInSecondFactor): SelectedSecondFactor | null => {
  if (factor.strategy === "totp") {
    return { strategy: "totp" };
  }
  if (factor.strategy === "backup_code") {
    return { strategy: "backup_code" };
  }
  if (factor.strategy === "phone_code") {
    return {
      strategy: "phone_code",
      phoneNumberId: factor.phoneNumberId,
      safeIdentifier: factor.safeIdentifier,
    };
  }
  // Clerk's SignInSecondFactor union also includes email_code / email_link.
  // Until we add UI for them, log so an unexpected enrollment shows up rather
  // than silently dropping the user into "no usable second factor."
  remiLogger.warn("Unsupported second factor strategy encountered.", { strategy: factor.strategy });
  return null;
};

// NIST 800-63B-4 treats SMS as restricted relative to TOTP, so prefer TOTP when both enrolled.
// Backup codes are never the default — they are the recovery path the user opts into.
export const pickDefaultSecondFactor = (options: readonly SelectedSecondFactor[]): SelectedSecondFactor | null =>
  options.find((o) => o.strategy === "totp") ?? options.find((o) => o.strategy === "phone_code") ?? null;

export const listSecondFactorOptions = (
  factors: readonly SignInSecondFactor[] | null | undefined,
): SelectedSecondFactor[] => (factors ?? []).map(toSelected).filter((factor) => factor !== null);

export const factorKey = (factor: SelectedSecondFactor): string =>
  factor.strategy === "phone_code" ? `phone_code:${factor.phoneNumberId}` : factor.strategy;

type ResetPasswordAction =
  | { action: "mfaRequired"; factors: SelectedSecondFactor[] }
  | { action: "complete"; sessionId: string }
  | { action: "error"; reason: "missing_session_id" | "unexpected_status" };

export const handleResetPasswordResult = (res: {
  status: SignInStatus | null;
  createdSessionId?: string | null;
  supportedSecondFactors?: readonly SignInSecondFactor[] | null;
}): ResetPasswordAction => {
  if (res.status === "needs_second_factor") {
    return { action: "mfaRequired", factors: listSecondFactorOptions(res.supportedSecondFactors) };
  }
  if (res.status === "complete") {
    if (!res.createdSessionId) {
      return { action: "error", reason: "missing_session_id" };
    }
    return { action: "complete", sessionId: res.createdSessionId };
  }
  return { action: "error", reason: "unexpected_status" };
};

const FACTOR_LABELS: Record<SelectedSecondFactor["strategy"], string> = {
  totp: strings.shared.auth.mfa.factorLabel.totp,
  phone_code: strings.shared.auth.mfa.factorLabel.phoneCode,
  backup_code: strings.shared.auth.mfa.factorLabel.backupCode,
};

export const secondFactorLabel = (strategy: SelectedSecondFactor["strategy"]): string => FACTOR_LABELS[strategy];
